import { Box } from "@chakra-ui/react";
import React from "react";
import Balancer from "react-wrap-balancer";
import {
  AlertIcon,
  CalendarIcon,
  ClockIcon,
  ColumnsIcon,
  CornerDownRightIcon,
  ErrorIcon,
  GlobeIcon,
  HeartIcon,
  LayoutIcon,
  LifeBuoyIcon,
  ListIcon,
  MinusCircleIcon,
  MobilePhoneIcon,
  MoneyIcon,
  PeopleIcon,
  PercentIcon,
  PersonIcon,
  PersonWithCheckIcon,
  PinIcon,
  RefreshIcon,
  SettingsIcon,
  SparklesIcon,
  TargetIcon,
  TrendIcon,
} from "ui/icons";
import hero from "../../../../public/assets/cdp-industries/heros/insurance.png";
import { dataBreachStat, personalizedExperiencesStat, type CdpIndustryConfig } from "./shared";

export const insuranceCdpConfig: CdpIndustryConfig = {
  icon: LifeBuoyIcon,
  name: "insurance",
  pluralNoun: "insurance companies",
  color: "green",
  seoCopy:
    "Leverage all your insurance data, including policyholders, claims, premium schedules, risk assessments, compliance logs, and fraud cases.",
  heroImage: {
    src: hero,
    alt: "A photograph of an insurance agent and customer with a list of example data tables overlayed. The data tables included are for policyholders, premium schedules, claims, risk assessments, and fraud cases.",
  },
  adoptingCdpsDescription:
    "Insurance is shifting to digital-first experiences, and customers value transparency, price, and convenience. If you’re not using your data to build trust and meet customer expectations, they’ll churn to one of your competitors.",
  featuredOutcomes: [
    {
      icon: TrendIcon,
      color: "blue",
      title: "Grow LTV",
    },
    {
      icon: PeopleIcon,
      color: "yellow",
      title: "Increase multi-policy adoption",
    },
    {
      icon: HeartIcon,
      color: "red",
      title: "Build loyalty",
    },
  ],
  dataStats: [
    {
      title: "$6.8T",
      description: (
        <Balancer>
          estimated market size <Box as="br" display={{ base: "block", lg: "none" }} />
          for the insurance industry
        </Balancer>
      ),
      href: "https://www.statista.com/markets/414/topic/461/insurance/#:~:text=Market%20size,-Global%20insurance%20premiums&text=While%20estimates%20can%20vary%2C%20most,insurance%20premiums%20written%20in%202022.",
    },
    personalizedExperiencesStat,
    dataBreachStat,
  ],
  personalizationCards: [
    {
      title:
        "Cigna uses machine learning and predictive modeling to power proactive and preventive outreach for better patient outcomes at a lower cost",
      href: "https://newsroom.cigna.com/using-data-and-predictive-analytics-to-improve-health-care",
    },
    {
      title: "The Zebra orchestrates complex user journeys across various channels like SMS and email",
      href: "/customers/the-zebra",
    },
    {
      title:
        "Allstate analyzes customer data from 16 million households to make churn predictions and enable faster and more personalized claim resolution",
      href: "https://domino.ai/customers/allstate",
    },
  ],
  useCases: {
    performanceMarketing: [
      // First row
      {
        icon: PersonWithCheckIcon,
        title: "Acquisition campaigns",
        description:
          "Drive new policy sign-ups by using demographic and behavioral data to target individuals and lookalike audiences searching for insurance plans, increasing new customer acquisition.",
      },
      {
        icon: MinusCircleIcon,
        title: "Suppression campaigns",
        description:
          "Reduce wasted ad spend by suppressing current policyholders from acquisition campaigns using CRM data, reallocating budget toward competitive policyholders to maximize efficiency.",
      },
      {
        icon: TargetIcon,
        title: "Retargeting campaigns",
        description:
          "Re-engage users who explored policies but didn’t convert by serving dynamic ads highlighting benefits, discounts, or personalized policy recommendations.",
      },
      // Second row
      {
        icon: CalendarIcon,
        title: "Seasonal promotions",
        description:
          "Boost policy purchases during high-need periods by promoting life insurance during tax season or home insurance ahead of disaster-prone seasons.",
      },
      {
        icon: SettingsIcon,
        title: "Conversion APIs",
        description:
          "Share online and offline conversion data with ad platforms, enabling algorithms to better identify and target users with a higher likelihood of converting to improve campaign performance.",
      },
      {
        icon: GlobeIcon,
        title: "Media networks",
        description:
          "Monetize customer insights by offering anonymized audience segments to financial service partners for targeted advertising, generating incremental revenue.",
      },
    ],
    lifecycleMarketing: [
      // First row
      {
        icon: MoneyIcon,
        title: "Upsell & cross-sell campaigns",
        description:
          "Recommend complementary policies such as life, renters, or home insurance to increase customer policy count.",
      },
      {
        icon: RefreshIcon,
        title: "Reactivation campaigns",
        description: "Offer incentives like waived fees or loyalty discounts to win back lapsed policyholders.",
      },
      {
        icon: CalendarIcon,
        title: "Seasonal campaigns",
        description:
          "Promote policies that align with seasonal needs, such as travel insurance for summer vacations or home insurance for wildfire season.",
      },
      // Second row
      {
        icon: ListIcon,
        title: "Claims experience campaigns",
        description:
          "Provide proactive updates, customer support guides, or automated claims tracking to improve satisfaction during claims processing.",
      },
      {
        icon: HeartIcon,
        title: "Loyalty rewards campaigns",
        description:
          "Reward long-term policyholders with renewal discounts, added benefits, or tiered loyalty rewards.",
      },
      {
        icon: PinIcon,
        title: "Localized promotions",
        description:
          "Offer specialized coverage for location-specific risks such as earthquake, flood, or hurricane insurance.",
      },
      // Third row
      {
        icon: PeopleIcon,
        title: "Family plan campaigns",
        description: "Promote family insurance bundles with discounts for covering multiple members under one policy.",
      },
      {
        icon: MobilePhoneIcon,
        title: "Digital adoption campaigns",
        description:
          "Encourage policyholders to use self-service apps for billing, claims management, and policy adjustments.",
      },
      {
        icon: AlertIcon,
        title: "Upcoming renewals",
        description:
          "Send reminders with exclusive renewal benefits to prevent churn and secure long-term customer retention.",
      },
    ],
    onsitePersonalization: [
      // First row
      {
        icon: LayoutIcon,
        title: "Dynamic home page content",
        description:
          "Display tailored policy recommendations, claims status updates, or renewal reminders to increase engagement and conversions.",
      },
      {
        icon: CornerDownRightIcon,
        title: "Next best action",
        description:
          "Suggest actions like enrolling in autopay, bundling policies, or updating coverage limits to drive engagement and incremental revenue.",
      },
      {
        icon: ClockIcon,
        title: "Claims progress tracking",
        description:
          "Provide real-time updates on claims processing through dashboards to enhance customer satisfaction and reduce service-related churn.",
      },
      // Second row
      {
        icon: AlertIcon,
        title: "Proactive renewal nudges",
        description:
          "Notify users about upcoming policy renewals and offer exclusive discounts for early renewals to improve retention and reduce administrative costs.",
      },
      {
        icon: SparklesIcon,
        title: "Gamified rewards",
        description:
          "Incentivize positive behaviors such as safe driving or wellness goals by offering rewards, discounts, or points.",
      },
      {
        icon: ErrorIcon,
        title: "Localized risk notifications",
        description:
          "Send location-based alerts about upcoming weather risks like storms or floods to improve trust and encourage timely policy adoption.",
      },
      // Third row
      {
        icon: ColumnsIcon,
        title: "Customized policy comparison tools",
        description:
          "Provide interactive tools for comparing coverage options, deductibles, and premiums to simplify decision-making and boost conversions.",
      },
      {
        icon: PersonIcon,
        title: "Self-service policy updates",
        description:
          "Enable users to make quick policy adjustments, such as adding dependents or changing payment methods, reducing support costs and improving convenience.",
      },
      {
        icon: PercentIcon,
        title: "Coverage & bundle recommendations",
        description:
          "Suggest tailored insurance bundles, coverage upgrades, or discounts based on life events, policy history, and risk profile to increase conversions and customer retention.",
      },
    ],
  },
  considerations: {
    dataModeling: {
      traditional: "Uses predefined models that may not fully capture policy types claims data or renewal patterns",
      composable: "Supports tailored models to handle insurance policies, claims histories, and customer risk profiles",
    },
    audienceManagement: {
      traditional:
        "Supports broad segmentation but struggles with identifying policyholder segments or claim history trends",
      composable: "Enables granular segmentation using claims, frequency renewal behaviors, and policy types",
    },
    customerJourneys: {
      traditional: "Provides standard templates for campaigns like renewal notices or cross-sell offers",
      composable:
        "Powers fully adaptable journeys for events like claims, follow-ups, new policy adoption, and churn prevention",
    },
    identityResolution: {
      traditional:
        "Relies on out-of-the-box algorithms that may not unify profiles across claims systems and underwriting platforms",
      composable:
        "Supports custom algorithms to unify profiles across claims databases, policy accounts, and underwriting systems",
    },
  },
  whyHightouchTitleMaxW: "750px",
  audienceOverlap: {
    title: "High-premium policyholders at risk of churn",
    firstCategoryLabel: "Policyholders with expiring high-premium plans",
    secondCategoryLabel: "Customers who missed renewal reminders",
  },
};
